import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Sparkles, X, Loader2, ExternalLink } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { useSalonAiContext } from '@/hooks/useSalonAiContext';
import { hasAiFeatures } from '@/lib/planFeatures';
import AssistantChat from './AssistantChat';

/**
 * Assistente AI globale: bottone flottante in basso a destra su tutte le pagine
 * della dashboard (tranne /assistant, che ha già la chat), apre un pannello da destra.
 * Solo per il titolare, dal piano AI in su.
 */
const AssistantWidget: React.FC = () => {
    const { isOwner, subscriptionPlan } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const { salonContext, ready } = useSalonAiContext(open);

    if (!isOwner || !hasAiFeatures(subscriptionPlan) || location.pathname.startsWith('/assistant')) return null;

    return (
        <>
            {/* Bottone flottante luminescente (glow statico, si anima solo su hover/tap) */}
            {/* Stesso margine dal basso e da destra: sotto non c'è nessuna barra da scavalcare */}
            <div className="fixed bottom-5 right-5 md:bottom-6 md:right-6 z-[8000]">
                <span
                    aria-hidden
                    className="absolute inset-0 rounded-full bg-gradient-to-tr from-clip-400 via-orange-400 to-clip-500 blur-lg opacity-70"
                />
                <motion.button
                    onClick={() => setOpen(true)}
                    title="Assistente AI"
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.94 }}
                    className="relative w-14 h-14 rounded-full bg-gradient-to-br from-clip-400 via-clip-500 to-clip-600 text-white shadow-xl shadow-clip-400/50 ring-1 ring-white/40 flex items-center justify-center"
                >
                    <Sparkles size={24} />
                </motion.button>
            </div>

            {/* Pannello da destra */}
            <AnimatePresence>
                {open && (
                    <div className="fixed inset-0 z-[8500]">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            onClick={() => setOpen(false)}
                            className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm"
                        />
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', duration: 0.45, bounce: 0.1 }}
                            className="absolute top-0 right-0 h-full w-full sm:w-[440px] bg-white shadow-2xl flex flex-col"
                        >
                            <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100 shrink-0">
                                <div>
                                    <h2 className="text-base font-bold text-gray-900 leading-none">Assistente AI</h2>
                                    <p className="text-xs text-gray-400 mt-1">Domande sui dati del tuo salone</p>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => { setOpen(false); navigate('/assistant'); }}
                                        title="Apri la pagina Assistente"
                                        className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                                    >
                                        <ExternalLink size={18} />
                                    </button>
                                    <button
                                        onClick={() => setOpen(false)}
                                        className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>
                            </div>
                            <div className="flex-1 min-h-0 p-4 sm:p-5">
                                {ready ? (
                                    <AssistantChat context={salonContext} />
                                ) : (
                                    <div className="h-full flex items-center justify-center text-gray-400 text-sm gap-2">
                                        <Loader2 size={16} className="animate-spin" /> Carico i dati del salone…
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </>
    );
};

export default AssistantWidget;
